import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, query, where, orderBy, onSnapshot, setDoc, updateDoc, getDocs, writeBatch } from "firebase/firestore";
import { Link, useNavigate, useParams } from "react-router-dom";
import { db } from "../firebase";

const dotToISOFlexible = (s) => {
  const m = /^(\d{2})\.(\d{2})(?:\.(\d{4}))?$/.exec((s || "").trim());
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const year = yyyy ?? String(new Date().getFullYear());
  return `${year}-${mm}-${dd}`;
};

const parseCommaList = (v) => String(v || "").split(/[,\n;]/g).map((x) => x.trim()).filter(Boolean);
const clampISO = (iso) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : "");
const safeArray = (v) => (Array.isArray(v) ? v : []);
const chunk = (arr, size) => { const out = []; for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size)); return out; };
const uniqBy = (arr, keyFn) => { const seen = new Set(); const out = []; for (const item of arr) { const k = keyFn(item); if (seen.has(k)) continue; seen.add(k); out.push(item); } return out; };
const cloneUsersForSession = (users = []) => safeArray(users).map((u) => ({ ...u, toBe: false, absence: false, refurbish: false }));


function ConfirmModal({ open, title, message, confirmText = "Potwierdź", cancelText = "Anuluj", onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div style={{position:"fixed",inset:0,backgroundColor:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:"16px"}} onMouseDown={onCancel}>
      <div style={{width:"min(520px, 100%)",background:"#0f1620",border:"1px solid #1d2a3a",borderRadius:"14px",boxShadow:"0 14px 60px rgba(0,0,0,.45)",padding:"14px"}} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
          <div style={{fontWeight:900,fontSize:"16px"}}>{title}</div>
          <button style={{marginLeft:"auto",background:"transparent",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"8px 10px",color:"#e8eef6",fontWeight:800,cursor:"pointer"}} onClick={onCancel}>✕</button>
        </div>
        <div style={{marginTop:"10px",fontSize:"13px",opacity:0.9,lineHeight:1.45}}>{message}</div>
        <div style={{display:"flex",gap:"10px",justifyContent:"flex-end",marginTop:"14px"}}>
          <button style={{background:"transparent",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 12px",color:"#e8eef6",fontWeight:800,cursor:"pointer"}} onClick={onCancel}>{cancelText}</button>
          <button style={{background:"#1f6feb",border:"1px solid rgba(255,255,255,.08)",borderRadius:"10px",padding:"10px 12px",color:"white",fontWeight:900,cursor:"pointer"}} onClick={onConfirm}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}

function useConfirm() {
  const [state, setState] = useState({ open: false, title: "", message: "", confirmText: "Potwierdź", cancelText: "Anuluj", resolve: null });

  const ask = ({ title, message, confirmText, cancelText }) =>
    new Promise((resolve) => setState({ open: true, title: title || "Potwierdzenie", message: message || "", confirmText: confirmText || "Potwierdź", cancelText: cancelText || "Anuluj", resolve }));

  const onConfirm = () => { state.resolve?.(true); setState((p) => ({ ...p, open: false, resolve: null })); };
  const onCancel = () => { state.resolve?.(false); setState((p) => ({ ...p, open: false, resolve: null })); };

  const modal = <ConfirmModal open={state.open} title={state.title} message={state.message} confirmText={state.confirmText} cancelText={state.cancelText} onConfirm={onConfirm} onCancel={onCancel} />;
  return { ask, modal };
}


function useFacilityGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "facility_groups"), where("facility_id", "==", "SPdqELFz2ovWSn8eFyws"));
    const unsub = onSnapshot(q, (snap) => { setGroups(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); });
    return () => unsub();
  }, []);

  return { groups, loading };
}

function useGroup(groupId) {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(Boolean(groupId));
  useEffect(() => {
    if (!groupId) { setGroup(null); setLoading(false); return; }
    setLoading(true);
    const unsub = onSnapshot(doc(db, "facility_groups", groupId), (snap) => { if (!snap.exists()) { setGroup(null); setLoading(false); return; } setGroup({ id: snap.id, ...snap.data() }); setLoading(false); });
    return () => unsub();
  }, [groupId]);
  return { group, loading };
}

function useGroupSessions({ groupId, fromISO, toISO }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(Boolean(groupId));
  useEffect(() => {
    if (!groupId) { setSessions([]); setLoading(false); return; }
    const base = [where("facility_group_id", "==", groupId), orderBy("date", "asc")];
    const clauses = [...base];
    if (fromISO) clauses.splice(1, 0, where("date", ">=", fromISO));
    if (toISO) clauses.splice(2, 0, where("date", "<=", toISO));
    const q = query(collection(db, "sessions"), ...clauses);
    setLoading(true);
    const unsub = onSnapshot(q, (snap) => { setSessions(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); }, (err) => { console.error("useGroupSessions error:", err); setSessions([]); setLoading(false); });
    return () => unsub();
  }, [groupId, fromISO, toISO]);
  return { sessions, loading };
}

async function createSessionsFromGroup({ group, dotDatesText }) {
  const dots = parseCommaList(dotDatesText);
  if (!dots.length) { alert('Podaj daty, np. "07.03, 14.03, 21.03"'); return; }
  const targetISOs = dots.map(dotToISOFlexible);
  if (targetISOs.some((x) => !x)) { alert("Jedna z dat jest zła. Format: DD.MM lub DD.MM.RRRR"); return; }
  const uniqueISOs = uniqBy(targetISOs, (x) => x);
  const instructors = safeArray(group?.instructors).length ? safeArray(group.instructors) : [group?.instructor].filter(Boolean);
  const day0 = safeArray(group?.days)[0] || {};

  await Promise.all(uniqueISOs.map(async (iso) => {
    const ref = doc(collection(db, "sessions"));
    await setDoc(ref, { id: ref.id, facility_group_id: group.id, facility_id: group.facility_id || "", location_id: group.location_id || "", color: group.color || "", name: group.name || "", exp: group.exp || "", type: group.type || "lesson", status: "scheduled", instructors, startTime: group.startTime || day0.startTime || "", endTime: group.endTime || day0.endTime || "", date: iso, users: cloneUsersForSession(group.users || []), changed: false, old_startTime: "", old_endTime: "", old_instructor: "", note: "" }, { merge: true });
  }));
}

async function addMemberToSessionsFromDate({ groupId, fromISO, newUser }) {
  const from = clampISO(fromISO);
  if (!groupId) return;
  if (!from) return alert("Wybierz poprawną datę OD (YYYY-MM-DD).");
  if (!newUser?.uid) return alert("Podaj uid osoby (wymagane).");

  const q = query(collection(db, "sessions"), where("facility_group_id", "==", groupId), where("date", ">=", from), orderBy("date", "asc"));
  const snap = await getDocs(q);
  const docs = snap.docs;

  const CHUNK = 450;
  for (const part of chunk(docs, CHUNK)) {
    const batch = writeBatch(db);
    part.forEach((d) => {
      const data = d.data();
      const users = safeArray(data.users);
      const exists = users.some((u) => u?.uid === newUser.uid);
      if (exists) return;
      batch.update(doc(db, "sessions", d.id), { users: [...users, { ...newUser, toBe: false, absence: false, refurbish: false }] });
    });
    await batch.commit();
  }
}

async function removeMemberFromSessionsFromDate({ groupId, fromISO, uid }) {
  const from = clampISO(fromISO);
  if (!groupId) return;
  if (!from) return alert("Wybierz poprawną datę OD (YYYY-MM-DD).");
  if (!uid) return alert("Podaj uid osoby do usunięcia.");

  const q = query(collection(db, "sessions"), where("facility_group_id", "==", groupId), where("date", ">=", from), orderBy("date", "asc"));
  const snap = await getDocs(q);
  const docs = snap.docs;

  const CHUNK = 450;
  for (const part of chunk(docs, CHUNK)) {
    const batch = writeBatch(db);
    part.forEach((d) => {
      const data = d.data();
      const users = safeArray(data.users);
      const nextUsers = users.filter((u) => u?.uid !== uid);
      if (nextUsers.length === users.length) return;
      batch.update(doc(db, "sessions", d.id), { users: nextUsers });
    });
    await batch.commit();
  }
}


export default function GroupsPage() {
  const navigate = useNavigate();
  const { groupId } = useParams();

  const { ask, modal } = useConfirm();
  const { groups, loading: loadingGroups } = useFacilityGroups();
  const { group, loading: loadingGroup } = useGroup(groupId);

  const [search, setSearch] = useState("");
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => {
      const name = String(g?.name || "").toLowerCase();
      const fid = String(g?.facility_id || "").toLowerCase();
      const lid = String(g?.location_id || "").toLowerCase();
      return name.includes(q) || fid.includes(q) || lid.includes(q) || String(g?.id || "").toLowerCase().includes(q);
    });
  }, [groups, search]);

  const [fromISO, setFromISO] = useState("");
  const [toISO, setToISO] = useState("");
  const { sessions, loading: loadingSessions } = useGroupSessions({ groupId, fromISO: clampISO(fromISO), toISO: clampISO(toISO) });

  const [edit, setEdit] = useState({ name: "", instructor: "", instructorsText: "", facility_id: "", location_id: "", exp: "", color: "" });
  useEffect(() => {
    if (!group) return;
    setEdit({ name: group?.name || "", instructor: group?.instructor || "", instructorsText: safeArray(group?.instructors).join(", "), facility_id: group?.facility_id || "", location_id: group?.location_id || "", exp: group?.exp || "", color: group?.color || "" });
  }, [group?.id]);

  const [usersJSON, setUsersJSON] = useState("[]");
  useEffect(() => {
    if (!group) return;
    try { setUsersJSON(JSON.stringify(safeArray(group?.users), null, 2)); } catch { setUsersJSON("[]"); }
  }, [group?.id]);

  const [locations, setLocations] = useState("")

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, "locations"), (snapshot) => {
          const collectionData = snapshot.docs.map(doc => doc?.data());
    
          let loc = collectionData
          let locs = []
          for (let i = 0; i < loc?.length; i++) {
            if (!loc[i]) return
            for (let x = 0; x < Object?.values(loc[i])?.length; x++) {
              locs?.push(Object?.values(loc[i])[x])
            }
          }
          setLocations(locs);
        });
    }, []);

  const [dotDatesText, setDotDatesText] = useState("07.03, 14.03, 21.03");
  const [massFromISO, setMassFromISO] = useState("");
  const [member, setMember] = useState({ uid: "", displayName: "", phone: "", gender: "girl", info: "" });

  const selectedGroupUsers = useMemo(() => safeArray(group?.users), [group?.users]);

  const saveGroupBasics = async () => {
    if (!group) return;

    let parsedUsers = [];
    try { const tmp = JSON.parse(usersJSON); if (!Array.isArray(tmp)) throw new Error("Users must be array"); parsedUsers = tmp; } catch { alert("Users JSON jest niepoprawny (musi być tablicą obiektów)."); return; }

    const instructors = parseCommaList(edit.instructorsText);
    await updateDoc(doc(db, "facility_groups", group.id), { name: edit.name || "", facility_id: edit.facility_id || "", location_id: edit.location_id || "", exp: edit.exp || "", color: edit.color || "", instructor: edit.instructor || "", instructors, users: parsedUsers });
    alert("Zapisano grupę ✅");
  };

  const handleCreateSessions = async () => {
    if (!group) return;
    const ok = await ask({ title: "Utworzyć sessions?", message: "Utworzę nowe dokumenty w kolekcji sessions dla podanych dat. Kontynuować?", confirmText: "Utwórz", cancelText: "Nie" });
    if (!ok) return;
    await createSessionsFromGroup({ group: { ...group, ...edit, instructors: parseCommaList(edit.instructorsText) }, dotDatesText });
    alert("Utworzono sessions ✅");
  };

  const handleMassAdd = async () => {
    if (!groupId) return;
    const ok = await ask({ title: "Dodać członka do sessions?", message: `Dodam osobę do wszystkich sessions (date >= ${massFromISO || "OD"}) w tej grupie. Kontynuować?`, confirmText: "Dodaj", cancelText: "Anuluj" });
    if (!ok) return;
    await addMemberToSessionsFromDate({ groupId, fromISO: massFromISO, newUser: member });
    alert("Dodano osobę do sessions ✅");
  };

  const handleMassRemove = async () => {
    if (!groupId) return;
    const ok = await ask({ title: "Usunąć członka z sessions?", message: `Usunę osobę ze wszystkich sessions (date >= ${massFromISO || "OD"}) w tej grupie. Kontynuować?`, confirmText: "Usuń", cancelText: "Anuluj" });
    if (!ok) return;
    await removeMemberFromSessionsFromDate({ groupId, fromISO: massFromISO, uid: member.uid });
    alert("Usunięto osobę z sessions ✅");
  };

  return (
    <div style={{display:"grid",gridTemplateColumns:"380px 1fr",gap:"14px",padding:"14px",minHeight:"calc(100vh - 20px)",background:"#0b0f14",color:"#e8eef6"}}>
      {/* LEFT */}
      <div style={{background:"#0f1620",border:"1px solid #1d2a3a",borderRadius:"12px",padding:"12px",boxShadow:"0 8px 30px rgba(0,0,0,.25)",display:"flex",flexDirection:"column",gap:"10px"}}>
        <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
          <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
            <h2 style={{margin:0,fontSize:"18px",fontWeight:800}}>Grupy</h2>
            <span style={{fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9}}>{loadingGroups ? "..." : `${groups.length}`}</span>
          </div>
          <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Szukaj (nazwa, facility_id, location_id, id)…" />
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:"8px",overflow:"auto",maxHeight:"calc(100vh - 140px)"}}>
          {loadingGroups && <div style={{fontSize:"12px",opacity:0.8}}>Ładowanie…</div>}
          {!loadingGroups && filteredGroups.length === 0 && <div style={{fontSize:"12px",opacity:0.8}}>Brak wyników.</div>}

          {filteredGroups.map((g) => {
            const active = g.id === groupId;
            const usersCount = safeArray(g?.users).length;
            return (
              <Link key={g.id} to={`/grupyzarzad/${g.id}`} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"10px",padding:"10px 10px",borderRadius:"10px",border:"1px solid #1d2a3a",background:active ? "rgba(31,111,235,.14)" : "#0b1118",textDecoration:"none",color:"#e8eef6"}}>
                <div style={{minWidth:0}}>
                  <div style={{fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{locations?.filter(x => x?.id === g?.location_id)[0]?.name} - {g?.days[0]?.name} {g?.days[0]?.dayOfWeek} - {g?.days[0]?.startTime} - {g?.days[0]?.endTime} - {g?.id}</div>
                  <div style={{fontSize:"12px",opacity:0.8}}>{g?.facility_id ? `facility: ${g.facility_id}` : "facility: —"}{g?.location_id ? ` • loc: ${g.location_id}` : ""}</div>
                </div>
                <span style={{fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9}}>{usersCount}</span>
              </Link>
            );
          })}

          {!groupId && !loadingGroups && groups.length > 0 && (
            <button style={{background:"transparent",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 12px",color:"#e8eef6",fontWeight:800,cursor:"pointer"}} onClick={() => navigate(`/grupy/${groups[0].id}`)}>
              Otwórz pierwszą grupę
            </button>
          )}
        </div>
      </div>

      {/* RIGHT */}
      <div style={{background:"#0f1620",border:"1px solid #1d2a3a",borderRadius:"12px",padding:"12px",boxShadow:"0 8px 30px rgba(0,0,0,.25)",display:"flex",flexDirection:"column",gap:"10px",overflow:"auto"}}>
        {!groupId && (
          <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
            <h2 style={{margin:0,fontSize:"18px",fontWeight:800}}>Wybierz grupę z listy</h2>
            <div style={{fontSize:"12px",opacity:0.8}}>Po prawej zobaczysz edycję bazowych danych, podgląd wydarzeń oraz masowe akcje.</div>
          </div>
        )}

        {groupId && (loadingGroup ? (
          <div style={{fontSize:"12px",opacity:0.8}}>Ładowanie grupy…</div>
        ) : !group ? (
          <div style={{fontSize:"12px",opacity:0.8}}>Nie znaleziono grupy.</div>
        ) : (
          <>
            <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
              <div style={{minWidth:0}}>
                <h2 style={{margin:0,fontSize:"18px",fontWeight:800}}>{group?.name || "(bez nazwy)"}</h2>
                <div style={{fontSize:"12px",opacity:0.8}}>groupId: <span style={{opacity:1}}>{group.id}</span></div>
              </div>
              <div style={{marginLeft:"auto",display:"flex",gap:"8px",flexWrap:"wrap"}}>
                <span style={{fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9}}>members: {selectedGroupUsers.length}</span>
                <span style={{fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9}}>sessions: {loadingSessions ? "..." : sessions.length}</span>
              </div>
            </div>

            {/* Basics */}
            <div style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"12px",padding:"12px",boxShadow:"0 8px 30px rgba(0,0,0,.18)"}}>
              <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
                <h3 style={{margin:0,fontSize:"16px",fontWeight:800}}>Bazowe dane grupy</h3>
                <button style={{marginLeft:"auto",background:"#1f6feb",border:"1px solid rgba(255,255,255,.08)",borderRadius:"10px",padding:"10px 12px",color:"white",fontWeight:900,cursor:"pointer"}} onClick={saveGroupBasics}>Zapisz zmiany</button>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginTop:"10px"}}>
                <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                  <label style={{fontSize:"12px",opacity:0.8}}>Nazwa</label>
                  <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={edit.name} onChange={(e) => setEdit((p) => ({ ...p, name: e.target.value }))} />
                  <label style={{fontSize:"12px",opacity:0.8}}>facility_id</label>
                  <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={edit.facility_id} onChange={(e) => setEdit((p) => ({ ...p, facility_id: e.target.value }))} />
                  <label style={{fontSize:"12px",opacity:0.8}}>location_id</label>
                  <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={edit.location_id} onChange={(e) => setEdit((p) => ({ ...p, location_id: e.target.value }))} />
                </div>

                <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                  <label style={{fontSize:"12px",opacity:0.8}}>instructor (legacy string)</label>
                  <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={edit.instructor} onChange={(e) => setEdit((p) => ({ ...p, instructor: e.target.value }))} />
                  <label style={{fontSize:"12px",opacity:0.8}}>instructors (lista, rozdziel przecinkami)</label>
                  <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={edit.instructorsText} onChange={(e) => setEdit((p) => ({ ...p, instructorsText: e.target.value }))} placeholder='np. "mail1@gmail.com, mail2@gmail.com"' />
                  <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
                    <div style={{flex:1}}>
                      <label style={{fontSize:"12px",opacity:0.8}}>exp</label>
                      <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={edit.exp} onChange={(e) => setEdit((p) => ({ ...p, exp: e.target.value }))} />
                    </div>
                    <div style={{width:"160px"}}>
                      <label style={{fontSize:"12px",opacity:0.8}}>color</label>
                      <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={edit.color} onChange={(e) => setEdit((p) => ({ ...p, color: e.target.value }))} placeholder="#11e8b7" />
                    </div>
                  </div>
                </div>
              </div>

              <div style={{marginTop:"10px"}}>
                <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
                  <h4 style={{margin:0,fontSize:"14px",fontWeight:800}}>Członkowie (facility_groups.users)</h4>
                  <span style={{fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9}}>{selectedGroupUsers.length}</span>
                </div>
                <div style={{fontSize:"12px",opacity:0.8}}>Edycja przez JSON (tablica obiektów). Typowo: uid, displayName, phone, gender, info…</div>
                <textarea style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%",minHeight:"90px",resize:"vertical",marginTop:"8px"}} value={usersJSON} onChange={(e) => setUsersJSON(e.target.value)} />
              </div>
            </div>

            {/* Create sessions */}
            <div style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"12px",padding:"12px",boxShadow:"0 8px 30px rgba(0,0,0,.18)"}}>
              <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
                <h3 style={{margin:0,fontSize:"16px",fontWeight:800}}>1) Dodaj sessions na podstawie grupy</h3>
                <button style={{marginLeft:"auto",background:"#1f6feb",border:"1px solid rgba(255,255,255,.08)",borderRadius:"10px",padding:"10px 12px",color:"white",fontWeight:900,cursor:"pointer"}} onClick={handleCreateSessions}>Utwórz sessions</button>
              </div>
              <div style={{fontSize:"12px",opacity:0.8,marginTop:"6px"}}>Daty: <b>DD.MM</b> lub <b>DD.MM.RRRR</b>, po przecinku / w nowych liniach.</div>
              <textarea style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%",minHeight:"70px",resize:"vertical",marginTop:"8px"}} value={dotDatesText} onChange={(e) => setDotDatesText(e.target.value)} placeholder="07.03, 14.03, 21.03" />
              <div style={{fontSize:"12px",opacity:0.8,marginTop:"8px"}}>Każda sesja dostaje <b>facility_group_id</b> = {group.id}.</div>
            </div>

            {/* Sessions list */}
            <div style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"12px",padding:"12px",boxShadow:"0 8px 30px rgba(0,0,0,.18)"}}>
              <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
                <h3 style={{margin:0,fontSize:"16px",fontWeight:800}}>5) Wydarzenia tej grupy</h3>
                <span style={{fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9,marginLeft:"auto"}}>{loadingSessions ? "..." : sessions.length}</span>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 120px",gap:"10px",marginTop:"10px"}}>
                <div>
                  <label style={{fontSize:"12px",opacity:0.8}}>OD (YYYY-MM-DD)</label>
                  <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} type="date" value={fromISO} onChange={(e) => setFromISO(e.target.value)} />
                </div>
                <div>
                  <label style={{fontSize:"12px",opacity:0.8}}>DO (YYYY-MM-DD)</label>
                  <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} type="date" value={toISO} onChange={(e) => setToISO(e.target.value)} />
                </div>
                <div style={{display:"flex",alignItems:"flex-end"}}>
                  <button style={{width:"100%",background:"transparent",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 12px",color:"#e8eef6",fontWeight:800,cursor:"pointer"}} onClick={() => { setFromISO(""); setToISO(""); }}>Wyczyść</button>
                </div>
              </div>

              <div style={{marginTop:"10px",maxHeight:"360px",overflow:"auto",display:"flex",flexDirection:"column",gap:"8px"}}>
                {loadingSessions && <div style={{fontSize:"12px",opacity:0.8}}>Ładowanie sessions…</div>}
                {!loadingSessions && sessions.length === 0 && <div style={{fontSize:"12px",opacity:0.8}}>Brak sessions dla tego filtra.</div>}
                {!loadingSessions && sessions.map((s) => {
                  const uCount = safeArray(s.users).length;
                  return (
                    <div key={s.id} style={{background:"#0f1620",border:"1px solid #1d2a3a",borderRadius:"12px",padding:"10px",boxShadow:"0 8px 30px rgba(0,0,0,.12)"}}>
                      <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
                        <div style={{fontWeight:900}}>{s.date} • {s.startTime || "--:--"}–{s.endTime || "--:--"} • {s.name || "(bez nazwy)"}</div>
                        <span style={{marginLeft:"auto",fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9}}>users: {uCount}</span>
                      </div>
                      <div style={{fontSize:"12px",opacity:0.8,marginTop:"4px"}}>sessionId: {s.id} • facility_id: {s.facility_id || "—"} • group_id: {s.facility_group_id || "—"}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mass actions */}
            <div style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"12px",padding:"12px",boxShadow:"0 8px 30px rgba(0,0,0,.18)"}}>
              <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}><h3 style={{margin:0,fontSize:"16px",fontWeight:800}}>3–4) Masowe dodawanie/usuwanie członka od daty</h3></div>

              <div style={{display:"grid",gridTemplateColumns:"240px 1fr",gap:"10px",marginTop:"10px"}}>
                <div>
                  <label style={{fontSize:"12px",opacity:0.8}}>OD (YYYY-MM-DD)</label>
                  <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} type="date" value={massFromISO} onChange={(e) => setMassFromISO(e.target.value)} />
                  <div style={{fontSize:"12px",opacity:0.8,marginTop:"8px"}}>Operacja: sessions z <b>date &gt;= OD</b> po <b>facility_group_id</b>.</div>
                </div>

                <div style={{display:"flex",flexDirection:"column",gap:"10px"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
                    <div>
                      <label style={{fontSize:"12px",opacity:0.8}}>uid (wymagane)</label>
                      <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={member.uid} onChange={(e) => setMember((p) => ({ ...p, uid: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{fontSize:"12px",opacity:0.8}}>phone</label>
                      <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={member.phone} onChange={(e) => setMember((p) => ({ ...p, phone: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{fontSize:"12px",opacity:0.8}}>displayName</label>
                      <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={member.displayName} onChange={(e) => setMember((p) => ({ ...p, displayName: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{fontSize:"12px",opacity:0.8}}>gender</label>
                      <select style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={member.gender} onChange={(e) => setMember((p) => ({ ...p, gender: e.target.value }))}>
                        <option value="girl">girl</option><option value="boy">boy</option><option value="other">other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{fontSize:"12px",opacity:0.8}}>info</label>
                    <input style={{background:"#0b1118",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 10px",color:"#e8eef6",outline:"none",width:"100%"}} value={member.info} onChange={(e) => setMember((p) => ({ ...p, info: e.target.value }))} />
                  </div>

                  <div style={{display:"flex",gap:"10px",alignItems:"center",flexWrap:"wrap"}}>
                    <button style={{background:"#1f6feb",border:"1px solid rgba(255,255,255,.08)",borderRadius:"10px",padding:"10px 12px",color:"white",fontWeight:900,cursor:"pointer"}} onClick={handleMassAdd}>Dodaj do sessions od daty</button>
                    <button style={{background:"transparent",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 12px",color:"#e8eef6",fontWeight:800,cursor:"pointer"}} onClick={handleMassRemove}>Usuń z sessions od daty</button>
                    <div style={{marginLeft:"auto",display:"flex",gap:"8px",flexWrap:"wrap"}}>
                      <span style={{fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9}}>group members: {selectedGroupUsers.length}</span>
                      <span style={{fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9}}>loaded sessions: {loadingSessions ? "..." : sessions.length}</span>
                    </div>
                  </div>

                  <div style={{marginTop:"8px"}}>
                    <div style={{fontSize:"12px",opacity:0.8}}>Szybkie uzupełnienie z bazowych członków grupy:</div>
                    <div style={{display:"flex",gap:"8px",flexWrap:"wrap",marginTop:"8px"}}>
                      {selectedGroupUsers.slice(0, 12).map((u) => (
                        <button key={u.uid} style={{background:"transparent",border:"1px solid #1d2a3a",borderRadius:"10px",padding:"10px 12px",color:"#e8eef6",fontWeight:800,cursor:"pointer"}} onClick={() => setMember((p) => ({ ...p, uid: u.uid || "", displayName: u.displayName || "", phone: u.phone || "", gender: u.gender || "girl", info: u.info || "" }))} title={u.uid}>
                          {u.displayName || "user"} <span style={{opacity:0.7,marginLeft:"6px",fontSize:"12px"}}>({String(u.uid || "").slice(0, 6)}…)</span>
                        </button>
                      ))}
                      {selectedGroupUsers.length > 12 && <span style={{fontSize:"12px",padding:"3px 8px",borderRadius:"999px",border:"1px solid #1d2a3a",background:"#0b1118",opacity:0.9}}>+{selectedGroupUsers.length - 12}</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{fontSize:"12px",opacity:0.8,marginTop:"10px"}}>🔧 Jeśli Firestore poprosi o index (facility_group_id + date), konsola pokaże link „Create index”.</div>
            </div>

            <div style={{fontSize:"12px",opacity:0.8}}>ℹ️ Nie musisz dopisywać pola <b>id</b> w facility_groups — UI używa <b>doc.id</b>. W sessions wymagane jest tylko <b>facility_group_id</b>.</div>
          </>
        ))}

        {modal}
      </div>
    </div>
  );
}